import { useState, useEffect, useMemo } from 'react';
import {
	CUSTOMER_SERVICE_PROJECT,
	SERVICE_DESK_PROJECT,
} from '@atlassian/jira-common-constants/src/project-types.tsx';
import { trackFetchErrors } from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import fetchJson from '@atlassian/jira-fetch/src/utils/as-json.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { getQueueUrl } from '@atlassian/jira-servicedesk-navigation/src/utils/queues/index.tsx';
import { toProjectKey } from '@atlassian/jira-shared-types/src/general.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import type {
	Project,
	DeepDiveItem,
	DeepDiveInfoItem,
	ItemDetails,
} from '../../../../common/types.tsx';
import { messages } from '../../messages.tsx';
import type {
	GetServiceDeskQueuesResponse,
	GetServiceDeskQueueCountResponse,
	ServiceDeskQueue,
} from './types.tsx';

const fetchServiceDeskQueues = (projectKey: string): Promise<GetServiceDeskQueuesResponse> =>
	fetchJson(`/rest/servicedesk/1/servicedesk/${projectKey}/queues/categories?deep-dive-sdNav=1`);

const fetchServiceDeskQueuesCount = (
	projectKey: string,
	queueId: string,
): Promise<GetServiceDeskQueueCountResponse> =>
	fetchJson(
		`/rest/servicedeskapi/servicedesk/${projectKey}/queue/${queueId}?includeCount=true&deep-dive-sdNav=1`,
	);

type BoardsAndFilter = {
	boards?: DeepDiveItem;
	filters?: DeepDiveInfoItem[];
	loading?: boolean;
	error?: boolean;
};

type BoardAndFilterResults = {
	[project: string]: BoardsAndFilter;
};

type RecentQueues = {
	[key: string]: ItemDetails[];
};

const MAX_SHOWN_QUEUES = 2;
export const QUEUE_DROPDOWN_CATEGORY = 'no-category';

const calculateAdditionalQueuesNeeded = (max: number, current: number) =>
	max - Math.min(max, current);

const padRecentQueues = (recentQueues: ItemDetails[], padding: DeepDiveInfoItem[]) => {
	const queues = recentQueues || [];
	const numPaddingNeeded = calculateAdditionalQueuesNeeded(MAX_SHOWN_QUEUES, queues.length);
	return [
		...queues.slice(0, MAX_SHOWN_QUEUES).map((queue) => ({
			id: queue.id.toString(),
			content: queue.title || '',
			href: queue.href || '',
			isLoading: true,
			itemCount: -1,
		})),
		...padding.slice(0, numPaddingNeeded),
	] as const;
};

const getInitialBoardState = (serviceProjects: Project[], recentQueues: RecentQueues) =>
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	serviceProjects.reduce<Record<string, any>>(
		(acc, { id: projectId, key }) => ({
			// eslint-disable-next-line jira/js/no-reduce-accumulator-spread
			...acc,
			[key]: {
				loading: true,
				error: false,
				filters: padRecentQueues(
					recentQueues[`project_${projectId}`],
					new Array(MAX_SHOWN_QUEUES).fill(0).map(() => ({
						id: '',
						content: '',
						href: '',
						isLoading: true,
						itemCount: -1,
					})),
				),
			},
		}),
		{},
	);

const updateBoardsFilterIssueCount = (
	board: BoardsAndFilter | null | undefined,
	filterId: string,
	issueCount: number,
	isLoading: boolean,
	error: boolean,
) => {
	if (!board) {
		return {};
	}
	const { filters, ...rest } = board;
	const newFilters =
		(filters &&
			filters.map((filter) => {
				const newFilter = { ...filter };
				if (newFilter.id === filterId) {
					newFilter.itemCount = issueCount;
					newFilter.isLoading = isLoading;
					newFilter.error = error;
				}
				return newFilter;
			})) ||
		[];
	return {
		...rest,
		filters: newFilters,
	};
};

const getQueuesFromCategory = (
	{ categories }: GetServiceDeskQueuesResponse,
	categoryKey: string,
): ServiceDeskQueue[] => categories.find((category) => category.key === categoryKey)?.queues || [];

const useFetchQueues = (recentProjects: Project[], recentQueues: RecentQueues) => {
	const queuesHeadingGalaxia = expVal('project-terminology-refresh', 'isEnabled', false)
		? messages.queuesHeadingGalaxia
		: messages.queuesHeading;
	const { formatMessage } = useIntl();
	const serviceProjects = useMemo(
		() =>
			recentProjects.filter(
				(project) =>
					project.projectType === SERVICE_DESK_PROJECT ||
					(project.projectType === CUSTOMER_SERVICE_PROJECT && fg('csm_rename_jcs_to_csm')),
			),
		[recentProjects],
	);
	const [results, setResults] = useState<BoardAndFilterResults>(
		getInitialBoardState(serviceProjects, recentQueues),
	);
	const heading = formatMessage(queuesHeadingGalaxia);

	useEffect(() => {
		if (!serviceProjects) {
			// Replace with lodash/noop
			// eslint-disable-next-line @typescript-eslint/no-empty-function
			return () => {};
		}
		let didCancel = false;

		// Set initial data
		setResults(getInitialBoardState(serviceProjects, recentQueues));

		// Fetch the count for a recent queue
		// @ts-expect-error - TS7031 - Binding element 'id' implicitly has an 'any' type. | TS7031 - Binding element 'key' implicitly has an 'any' type.
		const handleFetchingQueueCount = async ({ id, key }) => {
			try {
				const { issueCount } = await fetchServiceDeskQueuesCount(key, id);

				!didCancel &&
					setResults((resultsState) => {
						const board = resultsState && resultsState[key];
						const newBoard = updateBoardsFilterIssueCount(board, id, issueCount, false, false);
						return {
							...resultsState,
							[key]: newBoard,
						};
					});
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (fetchError: any) {
				trackFetchErrors({
					error: fetchError,
					id: 'fetchQueueCount',

					packageName: 'deepDiveCards',
				});
				!didCancel &&
					setResults((resultsState) => {
						const board = resultsState && resultsState[key];
						const newBoard = updateBoardsFilterIssueCount(board, id, -1, false, true);
						return {
							...resultsState,
							[key]: {
								...newBoard,
							},
						};
					});
			}
		};

		// Fetch the queues for a service desk project
		const handleFetchingServiceDeskQueues = async ({ id: projectId, key }: Project) => {
			try {
				const queuesByCategory = await fetchServiceDeskQueues(key);
				const queuesForDropdown = getQueuesFromCategory(queuesByCategory, QUEUE_DROPDOWN_CATEGORY);

				// Recent queues provided by caller of deep-dive-card (recent2QueuesByRecentProjects prop)
				const recentProjectQueues = recentQueues[`project_${projectId}`] || [];
				const recentQueueItems = recentProjectQueues.map(({ id, title, href }) => ({
					id: id.toString(),
					title,
					key,
					href,
				}));

				const numAdditionalFiltersNeeded = calculateAdditionalQueuesNeeded(
					MAX_SHOWN_QUEUES,
					recentQueueItems.length,
				);

				// extra queues fetched from 'no-category'
				const extraFilters = queuesForDropdown
					.filter(
						({ id }) => !recentQueueItems.find((recentFilter) => recentFilter.id === id.toString()),
					)
					.map(({ id, name }) => ({
						id: id.toString(),
						title: name,
						key,
						href: getQueueUrl({
							projectKey: toProjectKey(key),
							category: QUEUE_DROPDOWN_CATEGORY,
							queueId: Number(id),
						}),
					}))
					.slice(0, numAdditionalFiltersNeeded);

				const filtersItems = [...recentQueueItems, ...extraFilters];
				const filters = filtersItems.map(({ id, title, href }) => ({
					id,
					content: title,
					href,
					isLoading: true,
					itemCount: -1,
				}));

				const boardItems = queuesForDropdown.map(({ id, name }) => ({
					content: name,
					href: getQueueUrl({
						projectKey: toProjectKey(key),
						category: QUEUE_DROPDOWN_CATEGORY,
						queueId: Number(id),
					}),
					type: 'serviceDesk',
				}));

				!didCancel &&
					// @ts-expect-error - TS2345 - Argument of type '(resultsState: BoardAndFilterResults) => { [x: string]: BoardsAndFilter | { loading: false; error: false; boards: { heading: string; items: { content: string; href: string; type: string; }[]; }; filters: { ...; }[]; }; }' is not assignable to parameter of type 'SetStateAction<BoardAndFilterResults>'.
					setResults((resultsState) => ({
						...resultsState,
						[key]: {
							loading: false,
							error: false,
							boards: {
								heading,
								items: boardItems,
							},
							filters,
						},
					}));

				await Promise.all(filtersItems.map(handleFetchingQueueCount));

				// Fetch queue count
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (fetchError: any) {
				trackFetchErrors({
					error: fetchError,
					id: 'fetchServiceDeskQueues',

					packageName: 'deepDiveCards',
				});
				!didCancel &&
					setResults((resultsState) => ({
						...resultsState,
						[key]: {
							loading: false,
							error: true,
						},
					}));
			}
		};

		// Load Data
		(async () => {
			// Load list of queues
			await Promise.all(serviceProjects.map(handleFetchingServiceDeskQueues));
		})();

		return () => {
			didCancel = true;
		};

		// Disabling rule to use JSON.stringify to ensure that the nav items
		// are fetched only when project keys are added/removed/reordered
		// eslint-disable-next-line react-hooks/react-compiler
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		heading,
		// eslint-disable-next-line react-hooks/react-compiler
		// eslint-disable-next-line react-hooks/exhaustive-deps
		JSON.stringify(recentQueues),
		// eslint-disable-next-line react-hooks/react-compiler
		// eslint-disable-next-line react-hooks/exhaustive-deps
		JSON.stringify(serviceProjects.map((p) => p.key)),
	]);

	return results;
};

export default useFetchQueues;
